import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, Briefcase, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNavigationSearch, SearchResult } from '@/hooks/useNavigationSearch';
import { useRecentSearches } from '@/hooks/useRecentSearches';
import { useSearchAutocomplete } from '@/hooks/useSearchAutocomplete';
import { cn } from '@/lib/utils';
import engineTurbineBase from '@/assets/engine-turbine-base.png';
import partsPackageIcon from '@/assets/parts-package-icon.png';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';
import SearchSuggestions from '@/components/SearchSuggestions';

interface NavigationSearchProps {
  className?: string;
}

const NavigationSearch: React.FC<NavigationSearchProps> = ({ className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { results, isLoading, hasResults } = useNavigationSearch(query);
  const { recentSearches, addSearch, clearSearches } = useRecentSearches();
  const { suggestions: autocompleteSuggestions } = useSearchAutocomplete(query);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleResultClick = (result: SearchResult) => {
    addSearch(query);
    let assetParam = '';
    if (result.assetType === 'engine') assetParam = `&engine=${result.id}`;
    else if (result.assetType === 'parts') assetParam = `&parts=${result.id}`;
    else if (result.assetType === 'jobs') assetParam = `&job=${result.id}`;
    else if (result.assetType === 'services') assetParam = `&service=${result.id}`;
    
    navigate(`/marketplace?asset=${result.assetType}&listing=${result.id}${assetParam}&view=map`);
    setIsOpen(false);
    setQuery('');
  };

  const handleClose = () => {
    setIsOpen(false);
    setQuery('');
  };

  const handleViewAll = (assetType: string) => {
    addSearch(query);
    navigate(`/marketplace?search=${encodeURIComponent(query)}&asset=${assetType}&view=list`);
    setIsOpen(false);
    setQuery('');
  };

  const showDropdown = isOpen;
  const showResults = query.length >= 2;
  const showSuggestions = !showResults;

  const handleSuggestionClick = (term: string) => {
    setQuery(term);
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Always-visible search input */}
      <div className="flex items-center gap-2">
        <div className="relative" onClick={() => setIsOpen(true)}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search listings..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            className="w-[clamp(14rem,20vw,18rem)] pl-9 pr-8 h-10 bg-background/80 border-2 border-border/50 rounded-full shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/20 text-sm"
          />
          {isLoading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
          )}
          {query && !isLoading && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Clear search"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              className="absolute right-0.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground h-7 w-7"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Results dropdown */}
      {showDropdown && (
        <div className="absolute top-full right-0 mt-2 w-[360px] bg-popover rounded-lg shadow-xl border border-border overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-50">
          {showSuggestions ? (
            <SearchSuggestions
              onSuggestionClick={handleSuggestionClick}
              recentSearches={recentSearches}
              onClearRecent={clearSearches}
              autocompleteSuggestions={autocompleteSuggestions}
              query={query}
            />
          ) : isLoading && !hasResults ? (
            <div className="p-4 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              <span className="text-sm">Searching...</span>
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              {results.helicopters.length > 0 && (
                <ResultSection
                  title="Helicopters"
                  icon={<HelicopterSilhouetteIcon className="w-4 h-4" tone="nav" />}
                  results={results.helicopters}
                  onResultClick={handleResultClick}
                  onViewAll={() => handleViewAll('helicopter')}
                />
              )}
              {results.engines.length > 0 && (
                <ResultSection
                  title="Engines"
                  icon={<img src={engineTurbineBase} alt="" className="w-4 h-4 object-contain brightness-0 dark:invert" />}
                  results={results.engines}
                  onResultClick={handleResultClick}
                  onViewAll={() => handleViewAll('engine')}
                />
              )}
              {results.parts.length > 0 && (
                <ResultSection
                  title="Parts & Tools"
                  icon={<img src={partsPackageIcon} alt="" className="w-4 h-4 object-contain brightness-0 dark:invert" />}
                  results={results.parts}
                  onResultClick={handleResultClick}
                  onViewAll={() => handleViewAll('parts')}
                />
              )}
              {results.jobs.length > 0 && (
                <ResultSection
                  title="Jobs"
                  icon={<Briefcase className="w-4 h-4" />}
                  results={results.jobs}
                  onResultClick={handleResultClick}
                  onViewAll={() => handleViewAll('jobs')}
                />
              )}
              {results.services.length > 0 && (
                <ResultSection
                  title="Services"
                  icon={<Settings className="w-4 h-4" />}
                  results={results.services}
                  onResultClick={handleResultClick}
                  onViewAll={() => handleViewAll('services')}
                />
              )}
              {/* Zero-result recovery */}
              {!hasResults && !isLoading && query.length >= 2 && (
                <div className="p-3">
                  <p className="text-center text-muted-foreground text-sm mb-3">
                    No listings found for "{query}"
                  </p>
                  {autocompleteSuggestions.length > 0 && (
                    <div className="border-t border-border/50 pt-2">
                      <SearchSuggestions
                        onSuggestionClick={handleSuggestionClick}
                        autocompleteSuggestions={autocompleteSuggestions}
                        query={query}
                      />
                    </div>
                  )}
                  {autocompleteSuggestions.length === 0 && (
                    <SearchSuggestions onSuggestionClick={handleSuggestionClick} />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Result section component
interface ResultSectionProps {
  title: string;
  icon: React.ReactNode;
  results: SearchResult[];
  onResultClick: (result: SearchResult) => void;
  onViewAll?: () => void;
}

const ResultSection: React.FC<ResultSectionProps> = ({ title, icon, results, onResultClick, onViewAll }) => {
  return (
    <div className="border-b border-border/50 last:border-b-0">
      <div className="px-3 py-2 bg-muted/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {title} ({results.length >= 5 ? '5+' : results.length})
          </span>
        </div>
        {onViewAll && (
          <button
            onClick={onViewAll}
            className="text-xs font-medium text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
          >
            View all
            <Search className="w-3 h-3" />
          </button>
        )}
      </div>
      <div className="py-1">
        {results.map((result) => (
          <button
            key={result.id}
            onClick={() => onResultClick(result)}
            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted/50 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {/* Thumbnail with error fallback */}
            <div className="w-10 h-10 rounded-md bg-muted flex-shrink-0 overflow-hidden">
              {result.thumbnailUrl ? (
                <img
                  src={result.thumbnailUrl}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className="w-full h-full items-center justify-center text-muted-foreground/40"
                style={{ display: result.thumbnailUrl ? 'none' : 'flex' }}
              >
                {result.assetType === 'helicopter' && <HelicopterSilhouetteIcon className="w-5 h-5" tone="nav" />}
                {result.assetType === 'engine' && <img src={engineTurbineBase} alt="" className="w-5 h-5 object-contain opacity-40" />}
                {result.assetType === 'parts' && <img src={partsPackageIcon} alt="" className="w-5 h-5 object-contain opacity-40" />}
                {result.assetType === 'jobs' && <Briefcase className="w-5 h-5 opacity-40" />}
                {result.assetType === 'services' && <Settings className="w-5 h-5 opacity-40" />}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-popover-foreground truncate">
                {result.title}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {result.subtitle}
                {result.location && ` • ${result.location}`}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default NavigationSearch;
